import React, { useEffect, useState } from "react";
import { Typography, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PaidIcon from '@mui/icons-material/Paid';
import SmsIcon from '@mui/icons-material/Sms';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import ClientTypesReportDialog from "../../components/Reports/ClientTypesReportDialog/ClientTypesReportDialog";
import TopFiftyClientsReportDialog from "../../components/Reports/TopFiftyClientsReportDialog";
import QuarterlyUniqueClientsReportDialog from "../../components/Reports/QuarterlyUniqueClientsReportDialog";
import CurrentYearVsPreviousYearsReportDialog from "../../components/Reports/YearComparisonsReportDialog";
import ClientTypesMigrationReportDialog from "../../components/Reports/ClientTypesMigrationReportDialog";
import MonthlyRevenueReportDialog from "../../components/Reports/MonthlyRevenueReportDialog/MonthlyRevenueReportDialog";
import SMSReportDialog from "../../components/Reports/SMSReportDialog";
import CarsPerStationComparisonReportDialog from "../../components/Reports/CarsPerStationComparisonReportDialog";
import useReportsService from "../../services/useReportsService.ts";
import dayjs from "dayjs";
import ActionCard from "../../components/Common/ActionCard";
import { brand, cyan, green, orange, purple } from "../../components/AppTheme/themePrimitives";

export const reportThresholds = {
    clientTypes: 93,
    topFifty: 32,
    quarterlyCustomers: 184,
    yearComparison: 400,
    clientTypesMigration: 93,
    monthlyRevenue: 32,
    carsPerStation: 61,
};

export default function Reports() {
    const [daysPassed, setDaysPassed] = useState<number>(0);
    const [clientTypesReportDialogOpen, setClientTypesReportDialogOpen] = useState(false);
    const [topFiftyClientsReportDialogOpen, setTopFiftyClientsReportDialogOpen] = useState(false);
    const [quarterlyUniqueClientsReportDialogOpen, setQuarterlyUniqueClientsReportDialogOpen] = useState(false);
    const [currentYearVsPrevDialogOpen, setCurrentYearVsPrevDialogOpen] = useState(false);
    const [clientTypesMigrationReportDialogOpen, setClientTypesMigrationReportDialogOpen] = useState(false);
    const [monthlyRevenueReportDialogOpen, setMonthlyRevenueReportDialogOpen] = useState(false);
    const [smsReportDialogOpen, setSmsReportDialogOpen] = useState(false);
    const [carsPerStationReportDialogOpen, setCarsPerStationReportDialogOpen] = useState(false);

    const { t } = useTranslation("common");
    const { locations } = useAuth();
    const { getOldestEntry } = useReportsService();

    const carsPerStationDisabled = locations.some(loc => !loc.numstations);

    useEffect(() => {
        getOldestEntry().then(res => {
            setDaysPassed(res ? dayjs().diff(dayjs(res, "DD/MM/YYYY HH:mm:ss"), 'day') : 0);
        });
    }, [getOldestEntry]);

    const daysLeft = (threshold: number) => threshold - daysPassed;
    const isLocked = (threshold: number) => daysPassed < threshold;

    const items = [
        {
            title: t("reports.clientTypes.title"),
            description: t("reports.clientTypes.subtitle"),
            icon: <DonutLargeIcon />,
            accentColor: brand[400],
            disabled: isLocked(reportThresholds.clientTypes),
            disabledLabel: t("reports.common.daysUntilEnable", { days: daysLeft(reportThresholds.clientTypes) }),
            onClick: () => setClientTypesReportDialogOpen(true),
        },
        {
            title: t("reports.top-fifty-clients.title"),
            description: t("reports.top-fifty-clients.subtitle"),
            icon: <LeaderboardIcon />,
            accentColor: orange[400],
            disabled: isLocked(reportThresholds.topFifty),
            disabledLabel: t("reports.common.daysUntilEnable", { days: daysLeft(reportThresholds.topFifty) }),
            onClick: () => setTopFiftyClientsReportDialogOpen(true),
        },
        {
            title: t("reports.quarterlyUniqueClientsReport.title"),
            description: t("reports.quarterlyUniqueClientsReport.subtitle"),
            icon: <DateRangeIcon />,
            accentColor: cyan[400],
            disabled: isLocked(reportThresholds.quarterlyCustomers),
            disabledLabel: t("reports.common.daysUntilEnable", { days: daysLeft(reportThresholds.quarterlyCustomers) }),
            onClick: () => setQuarterlyUniqueClientsReportDialogOpen(true),
        },
        {
            title: t("reports.currentYearVsPreviousYears.title"),
            description: t("reports.currentYearVsPreviousYears.subtitle"),
            icon: <TrendingUpIcon />,
            accentColor: green[400],
            disabled: isLocked(reportThresholds.yearComparison),
            disabledLabel: t("reports.common.daysUntilEnable", { days: daysLeft(reportThresholds.yearComparison) }),
            onClick: () => setCurrentYearVsPrevDialogOpen(true),
        },
        {
            title: t("reports.clientTypesMigration.title"),
            description: t("reports.clientTypesMigration.subtitle"),
            icon: <SwapHorizIcon />,
            accentColor: purple[400],
            disabled: isLocked(reportThresholds.clientTypesMigration),
            disabledLabel: t("reports.common.daysUntilEnable", { days: daysLeft(reportThresholds.clientTypesMigration) }),
            onClick: () => setClientTypesMigrationReportDialogOpen(true),
        },
        {
            title: t("reports.monthlyRevenue.title"),
            description: t("reports.monthlyRevenue.subtitle"),
            icon: <PaidIcon />,
            accentColor: green[500],
            disabled: isLocked(reportThresholds.monthlyRevenue),
            disabledLabel: t("reports.common.daysUntilEnable", { days: daysLeft(reportThresholds.monthlyRevenue) }),
            onClick: () => setMonthlyRevenueReportDialogOpen(true),
        },
        {
            title: t("reports.carsPerStation.title"),
            description: t("reports.carsPerStation.subtitle"),
            icon: <DirectionsCarIcon />,
            accentColor: brand[300],
            disabled: carsPerStationDisabled || isLocked(reportThresholds.carsPerStation),
            disabledLabel: carsPerStationDisabled
                ? t("reports.carsPerStation.missingStationsCount")
                : t("reports.common.daysUntilEnable", { days: daysLeft(reportThresholds.carsPerStation) }),
            onClick: () => setCarsPerStationReportDialogOpen(true),
        },
        {
            title: t("reports.sms.title"),
            description: t("reports.sms.subtitle"),
            icon: <SmsIcon />,
            accentColor: cyan[300],
            onClick: () => setSmsReportDialogOpen(true),
        },
    ];

    return (
        <>
            <Box sx={{ width: "100%", height: "100%", padding: 2 }}>
                <Typography variant="h4" gutterBottom>
                    {t("navbar.reports")}
                </Typography>
                <Grid container spacing={2}>
                    {items.map((item, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                            <ActionCard
                                title={item.title}
                                description={item.description}
                                icon={item.icon}
                                accentColor={item.accentColor}
                                indicator={null}
                                disabled={item.disabled}
                                disabledLabel={item.disabledLabel}
                                onClick={item.disabled ? undefined : item.onClick}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
            {clientTypesReportDialogOpen && (
                <ClientTypesReportDialog open={clientTypesReportDialogOpen} onClose={() => setClientTypesReportDialogOpen(false)} />
            )}
            {topFiftyClientsReportDialogOpen && (
                <TopFiftyClientsReportDialog open={topFiftyClientsReportDialogOpen} onClose={() => setTopFiftyClientsReportDialogOpen(false)} />
            )}
            {quarterlyUniqueClientsReportDialogOpen && (
                <QuarterlyUniqueClientsReportDialog open={quarterlyUniqueClientsReportDialogOpen} onClose={() => setQuarterlyUniqueClientsReportDialogOpen(false)} />
            )}
            {currentYearVsPrevDialogOpen && (
                <CurrentYearVsPreviousYearsReportDialog open={currentYearVsPrevDialogOpen} onClose={() => setCurrentYearVsPrevDialogOpen(false)} />
            )}
            {clientTypesMigrationReportDialogOpen && (
                <ClientTypesMigrationReportDialog open={clientTypesMigrationReportDialogOpen} onClose={() => setClientTypesMigrationReportDialogOpen(false)} />
            )}
            {monthlyRevenueReportDialogOpen && (
                <MonthlyRevenueReportDialog open={monthlyRevenueReportDialogOpen} onClose={() => setMonthlyRevenueReportDialogOpen(false)} />
            )}
            {smsReportDialogOpen && (
                <SMSReportDialog open={smsReportDialogOpen} onClose={() => setSmsReportDialogOpen(false)} />
            )}
            {carsPerStationReportDialogOpen && (
                <CarsPerStationComparisonReportDialog open={carsPerStationReportDialogOpen} onClose={() => setCarsPerStationReportDialogOpen(false)} />
            )}
        </>
    );
}
